import { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { adminReq } from "../api";

const TABS = ["Keys", "Users", "Analytics", "Logs", "My Accounts", "Support"];

// Full keys were previously rendered in the DOM by default any time
// `revealed[k.id]` was falsy — the "Reveal" button looked like it gated
// visibility, but really did nothing until clicked, since the JSX fell
// straight through to the raw key. Genuinely masks the value unless
// explicitly revealed via the backend's /admin/reveal-key endpoint.
function maskKey(key) {
  if (!key) return "-";
  const str = String(key);
  if (str.length <= 8) return "••••••••";
  return `${str.slice(0, 4)}••••••••${str.slice(-4)}`;
}

export default function Admin({ onBack }) {
  const token = localStorage.getItem("zeb_token") || "";
  const [tab, setTab] = useState("Keys");
  const [copyMsg, setCopyMsg] = useState("");
  const [schema, setSchema] = useState(null);

  useEffect(() => {
    adminReq("GET", "/admin/schema-check", token).then(setSchema).catch(() => {});
  }, [token]);

  const flashCopy = (msg) => {
    setCopyMsg(msg);
    setTimeout(() => setCopyMsg(""), 2500);
  };

  return (
    <div style={{...styles.wrap, alignItems:"flex-start", padding:32}}>
      {copyMsg && <div style={styles.toast}>{copyMsg}</div>}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",width:"100%",marginBottom:20}}>
        <h1 style={{color:"var(--rbx-accent, #38bdf8)",margin:0,fontFamily:"monospace"}}>Admin Panel</h1>
        <button style={{...styles.btn,background:"#1a1a1a",color:"#888"}} onClick={onBack}>← Back</button>
      </div>

      {schema && !schema.ok && (
        <div style={{ width:"100%", boxSizing:"border-box", background:"#2a0f0f", border:"1px solid #7f1d1d", borderRadius:8, padding:"12px 16px", marginBottom:16, color:"#fca5a5", fontSize:12.5, lineHeight:1.7 }}>
          <b style={{ color:"#f87171" }}>Database is out of date.</b> {schema.fix}. Missing:
          {Object.entries(schema.problems || {}).map(([table, v]) => (
            <div key={table} style={{ color:"#ccc" }}>• <b>{table}</b>: {Array.isArray(v) ? v.join(", ") : v}</div>
          ))}
        </div>
      )}

      <div style={styles.tabBar}>
        {TABS.map(t => (
          <button key={t} style={{...styles.tabBtn, ...(tab === t ? styles.tabBtnActive : {})}} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Keys" && <KeysTab token={token} flashCopy={flashCopy} />}
      {tab === "Users" && <UsersTab token={token} flashCopy={flashCopy} />}
      {tab === "Analytics" && <AnalyticsTab token={token} />}
      {tab === "Logs" && <LogsTab token={token} />}
      {tab === "My Accounts" && <MyAccountsTab token={token} />}
      {tab === "Support"     && <SupportTab token={token} />}
    </div>
  );
}

// ── Keys ──────────────────────────────────────────────────
function KeysTab({ token, flashCopy }) {
  const [keys, setKeys] = useState([]);
  const [users, setUsers] = useState([]);
  const [newTier, setNewTier] = useState("lite");
  const [newNote, setNewNote] = useState("");
  const [qty, setQty] = useState(1);
  const [genKeys, setGenKeys] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [revealed, setRevealed] = useState({}); // key id -> full key string, once revealed
  const [showAll, setShowAll] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    const [k, u] = await Promise.all([
      adminReq("GET", "/admin/keys", token),
      adminReq("GET", "/admin/users", token),
    ]);
    setKeys(k); setUsers(u);
    setSelected(new Set());
    setRevealed({});
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await adminReq("POST", "/admin/generate-key", token, { tier: newTier, note: newNote, count: qty });
      const newKeys = res.keys || [res.key];
      setGenKeys(newKeys);
      try {
        await navigator.clipboard.writeText(newKeys.join("\n"));
        flashCopy(`✓ Copied ${newKeys.length} key${newKeys.length > 1 ? "s" : ""} to clipboard!`);
      } catch {
        flashCopy("Generated. Clipboard copy failed, copy manually below");
      }
      loadData();
    } finally {
      setGenerating(false);
    }
  };

  const copyKey = async (key) => {
    await navigator.clipboard.writeText(key);
    flashCopy("✓ Copied!");
  };

  const copyAllGenerated = async () => {
    await navigator.clipboard.writeText(genKeys.join("\n"));
    flashCopy(`✓ Copied ${genKeys.length} keys!`);
  };

  const reveal = async (id) => {
    if (revealed[id]) {
      setRevealed(prev => { const next = {...prev}; delete next[id]; return next; }); // toggle back to masked
      return;
    }
    const res = await adminReq("POST", "/admin/reveal-key", token, { id });
    setRevealed(prev => ({...prev, [id]: res.key}));
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this key?")) return;
    await adminReq("POST", "/admin/revoke-key", token, { id });
    loadData();
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === visibleKeys.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visibleKeys.map(k => k.id)));
    }
  };

  const deleteSelected = async () => {
    const activatedCount = keys.filter(k => selected.has(k.id) && k.activated).length;
    const msg = activatedCount > 0
      ? `${selected.size} key(s) selected, ${activatedCount} of which are already activated (have an account). Delete anyway?`
      : `Delete ${selected.size} key(s)? This can't be undone.`;
    if (!window.confirm(msg)) return;
    await adminReq("POST", "/admin/delete-keys", token, { ids: [...selected] });
    flashCopy(`✓ Deleted ${selected.size} key(s)`);
    loadData();
  };

  const filteredKeys = keys.filter(k => {
    if (statusFilter !== "all" && k.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return k.key?.toLowerCase().includes(s) || k.note?.toLowerCase().includes(s) || k.tier?.toLowerCase().includes(s);
    }
    return true;
  });

  // Don't dump the entire key list on screen by default — only show a
  // capped number unless the admin is actively searching/filtering for
  // something specific, or explicitly asks to see everything.
  const isFiltering = search.trim() !== "" || statusFilter !== "all";
  const CAP = 20;
  const visibleKeys = (isFiltering || showAll) ? filteredKeys : filteredKeys.slice(0, CAP);

  return (
    <>
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Generate Keys</h3>
        <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
          <select style={styles.select} value={newTier} onChange={e=>setNewTier(e.target.value)}>
            <option value="lite">Lite (1mo)</option>
            <option value="basic">Basic (1mo)</option>
            <option value="pro">Pro (1mo)</option>
            <option value="lifetime-lite">Lifetime Lite</option>
            <option value="lifetime-basic">Lifetime Basic</option>
            <option value="lifetime-pro">Lifetime Pro</option>
          </select>
          <div style={styles.qtyWrap}>
            <button style={styles.qtyBtn} onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
            <input
              style={styles.qtyInput}
              type="number"
              min={1} max={100}
              value={qty}
              onChange={e => setQty(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
            />
            <button style={styles.qtyBtn} onClick={() => setQty(q => Math.min(100, q + 1))}>+</button>
          </div>
          <input style={{...styles.input,width:200}} placeholder="Note (e.g. batch/buyer)" value={newNote} onChange={e=>setNewNote(e.target.value)} />
          <button style={styles.btn} onClick={generate} disabled={generating}>
            {generating ? "Generating..." : qty > 1 ? `Generate ${qty} Keys` : "Generate"}
          </button>
        </div>

        {genKeys.length > 0 && (
          <div style={styles.genResult}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10}}>
              <span style={{color:"#555", fontSize:12}}>{genKeys.length} key{genKeys.length > 1 ? "s" : ""} generated</span>
              {genKeys.length > 1 && (
                <button style={styles.copyAllBtn} onClick={copyAllGenerated}>Copy All</button>
              )}
            </div>
            <div style={styles.genList}>
              {genKeys.map(k => (
                <div key={k} style={styles.genRow}>
                  <code style={{fontSize:13, color:"var(--rbx-accent, #38bdf8)"}}>{k}</code>
                  <button style={styles.rowCopyBtn} onClick={() => copyKey(k)}>Copy</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={styles.section}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:10}}>
          <h3 style={{...styles.sectionTitle, margin:0}}>
            Keys ({visibleKeys.length}{visibleKeys.length !== keys.length ? ` of ${keys.length}` : ""})
          </h3>
          <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
            {selected.size > 0 && (
              <button style={styles.deleteBtn} onClick={deleteSelected}>
                🗑 Delete {selected.size} Selected
              </button>
            )}
            <input
              style={{...styles.input, width:200}}
              placeholder="Search key, note, tier..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div style={styles.filterGroup}>
              {["all","active","used","revoked"].map(f => (
                <button
                  key={f}
                  style={{...styles.filterBtn, ...(statusFilter === f ? styles.filterBtnActive : {})}}
                  onClick={() => setStatusFilter(f)}
                >
                  {f[0].toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>
                <input type="checkbox" checked={visibleKeys.length > 0 && selected.size === visibleKeys.length} onChange={toggleSelectAll} />
              </th>
              {["Key","Tier","Status","Activated","Has Account","Note","Created",""].map(h=><th key={h} style={styles.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {visibleKeys.map(k=>(
              <tr key={k.id} style={selected.has(k.id) ? {background:"#111"} : {}}>
                <td style={styles.td}>
                  <input type="checkbox" checked={selected.has(k.id)} onChange={() => toggleSelect(k.id)} />
                </td>
                <td style={styles.td}>
                  <code style={{fontSize:11}}>{revealed[k.id] ? revealed[k.id] : maskKey(k.key)}</code>
                  <button style={styles.revealBtn} onClick={() => reveal(k.id)}>{revealed[k.id] ? "Hide" : "Reveal"}</button>
                </td>
                <td style={styles.td}><span style={{color:k.tier?.includes("pro")?"#f59e0b":"#888"}}>{k.tier}</span></td>
                <td style={styles.td}><span style={{color:k.status==="active"?"var(--rbx-accent, #38bdf8)":"#f44"}}>{k.status}</span></td>
                <td style={styles.td}>
                  {k.activated
                    ? <span style={styles.badgeGreen}>✓ Activated</span>
                    : <span style={styles.badgeGray}>Unused</span>}
                </td>
                <td style={styles.td}>
                  {k.activated
                    ? (k.has_account
                        ? <span style={styles.badgeGreen}>✓ Has account</span>
                        : <span style={styles.badgeGray}>No account</span>)
                    : <span style={{color:"#333"}}>-</span>}
                </td>
                <td style={styles.td}>{k.note || "-"}</td>
                <td style={styles.td}>{new Date(k.created_at).toLocaleDateString()}</td>
                <td style={styles.td}>
                  <div style={{display:"flex", gap:6}}>
                    <button style={{...styles.btn,background:"#1a1a1a",color:"#888",fontSize:11,padding:"4px 10px"}} onClick={()=>copyKey(revealed[k.id] || k.key)} disabled={!revealed[k.id]}>Copy</button>
                    {k.status==="active" && <button style={{...styles.btn,background:"#7f1d1d",fontSize:11,padding:"4px 10px"}} onClick={()=>revoke(k.id)}>Revoke</button>}
                  </div>
                </td>
              </tr>
            ))}
            {visibleKeys.length === 0 && (
              <tr><td colSpan={9} style={{...styles.td, textAlign:"center", color:"#444", padding:24}}>No keys match your search/filter</td></tr>
            )}
          </tbody>
        </table>
        {!isFiltering && !showAll && filteredKeys.length > CAP && (
          <button style={{...styles.btn, background:"#1a1a1a", color:"#888", marginTop:12}} onClick={() => setShowAll(true)}>
            Show all {filteredKeys.length} keys
          </button>
        )}
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Users ({users.length})</h3>
        <table style={styles.table}>
          <thead>
            <tr>{["User ID","Tier","Key","Note","Joined"].map(h=><th key={h} style={styles.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {users.map(u=>(
              <tr key={u.id}>
                <td style={styles.td}><code style={{fontSize:10}}>{u.id}</code></td>
                <td style={styles.td}><span style={{color:u.tier?.includes("pro")?"#f59e0b":"#888"}}>{u.tier}</span></td>
                <td style={styles.td}><code style={{fontSize:10}}>{maskKey(u.keys?.key)}</code></td>
                <td style={styles.td}>{u.keys?.note || "-"}</td>
                <td style={styles.td}>{new Date(u.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Users ─────────────────────────────────────────────────
const TIER_META = {
  "lifetime-pro":   { label: "Lifetime Pro",   color: "#f59e0b", icon: "👑" },
  "lifetime-basic": { label: "Lifetime Basic",  color: "#f59e0b", icon: "👑" },
  "lifetime-lite":  { label: "Lifetime Lite",   color: "#f59e0b", icon: "👑" },
  "pro":            { label: "Pro",             color: "#38bdf8", icon: "⭐" },
  "basic":          { label: "Basic",           color: "#888",    icon: "·"  },
  "lite":           { label: "Lite",            color: "#555",    icon: "·"  },
};

function daysLeft(user) {
  if (!user.tier) return null;
  if (user.tier.startsWith("lifetime-")) return Infinity;
  if (!user.created_at) return null;
  const expiry = new Date(new Date(user.created_at).getTime() + 30 * 24 * 60 * 60 * 1000);
  return Math.ceil((expiry - Date.now()) / (24 * 60 * 60 * 1000));
}

function UsersTab({ token, flashCopy }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [editingTier, setEditingTier] = useState({}); // user_id -> selected tier value
  const [saving, setSaving] = useState({});           // user_id -> bool
  const [viewingUser, setViewingUser] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    const u = await adminReq("GET", "/admin/users", token);
    setUsers(u);
  };

  const saveTier = async (userId, tier) => {
    setSaving(s => ({...s, [userId]: true}));
    try {
      await adminReq("POST", "/admin/update-user-tier", token, { user_id: userId, tier });
      flashCopy(`✓ Tier updated to ${tier}`);
      setEditingTier(e => { const n = {...e}; delete n[userId]; return n; });
      load();
    } catch(e) {
      alert("Failed: " + e.message);
    } finally {
      setSaving(s => ({...s, [userId]: false}));
    }
  };

  const openView = async (uid) => {
    setViewLoading(true);
    try {
      const res = await adminReq("GET", `/admin/user-view/${uid}`, token);
      setViewingUser(res);
    } catch(e) {
      alert("Failed: " + e.message);
    } finally {
      setViewLoading(false);
    }
  };

  const filtered = users.filter(u => {
    if (tierFilter === "expired") {
      const d = daysLeft(u); return d !== null && d !== Infinity && d <= 0;
    }
    if (tierFilter === "lifetime") return u.tier?.startsWith("lifetime-");
    if (tierFilter === "monthly") return !u.tier?.startsWith("lifetime-");
    if (search) {
      const s = search.toLowerCase();
      return u.id?.toLowerCase().includes(s)
          || u.keys?.key?.toLowerCase().includes(s)
          || u.keys?.note?.toLowerCase().includes(s)
          || u.tier?.toLowerCase().includes(s);
    }
    return true;
  }).filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return u.id?.toLowerCase().includes(s)
        || u.keys?.key?.toLowerCase().includes(s)
        || u.keys?.note?.toLowerCase().includes(s)
        || u.tier?.toLowerCase().includes(s);
  });

  return (
    <>
      <div style={styles.section}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:10}}>
          <h3 style={{...styles.sectionTitle, margin:0}}>Users ({filtered.length}{filtered.length !== users.length ? ` of ${users.length}` : ""})</h3>
          <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
            <input
              style={{...styles.input, width:220}}
              placeholder="Search by user ID, key, note, tier..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div style={styles.filterGroup}>
              {[["all","All"],["lifetime","👑 Lifetime"],["monthly","Monthly"],["expired","⏰ Expired"]].map(([v,l]) => (
                <button key={v} style={{...styles.filterBtn, ...(tierFilter===v?styles.filterBtnActive:{})}} onClick={()=>setTierFilter(v)}>{l}</button>
              ))}
            </div>
          </div>
        </div>

        <table style={styles.table}>
          <thead>
            <tr>{["User","Key","Tier","Status","Joined","Actions"].map(h=><th key={h} style={styles.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.map(u => {
              const meta = TIER_META[u.tier] || {label:u.tier||"?", color:"#555", icon:"·"};
              const days = daysLeft(u);
              const expired = days !== null && days !== Infinity && days <= 0;
              const pendingTier = editingTier[u.id];
              return (
                <tr key={u.id} style={{background: expired ? "rgba(239,68,68,0.04)" : undefined}}>
                  <td style={styles.td}>
                    <div style={{fontFamily:"monospace", fontSize:10, color:"#555"}}>{u.id?.slice(0,12)}…</div>
                    {u.keys?.note && <div style={{fontSize:11, color:"#888", marginTop:2}}>{u.keys.note}</div>}
                  </td>
                  <td style={styles.td}>
                    <code style={{fontSize:10, color:"#555"}}>{maskKey(u.keys?.key)}</code>
                  </td>
                  <td style={styles.td}>
                    <span style={{color: meta.color, fontWeight:700, fontSize:12}}>{meta.icon} {meta.label}</span>
                  </td>
                  <td style={styles.td}>
                    {days === Infinity
                      ? <span style={{color:"#f59e0b", fontSize:11, fontWeight:700}}>♾ Lifetime</span>
                      : expired
                        ? <span style={{color:"#ef4444", fontSize:11, fontWeight:700}}>⏰ Expired</span>
                        : days !== null
                          ? <span style={{color: days <= 5 ? "#f59e0b" : "#4ade80", fontSize:11}}>{days}d left</span>
                          : <span style={{color:"#555", fontSize:11}}>-</span>
                    }
                  </td>
                  <td style={styles.td}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</td>
                  <td style={styles.td}>
                    <div style={{display:"flex", gap:6, alignItems:"center", flexWrap:"wrap"}}>
                      {pendingTier !== undefined ? (
                        <>
                          <select
                            style={{...styles.select, fontSize:11, padding:"4px 8px"}}
                            value={pendingTier}
                            onChange={e => setEditingTier(et => ({...et, [u.id]: e.target.value}))}
                          >
                            {Object.entries(TIER_META).map(([k,v]) => (
                              <option key={k} value={k}>{v.icon} {v.label}</option>
                            ))}
                          </select>
                          <button
                            style={{...styles.btn, fontSize:11, padding:"4px 10px", background:"#22c55e", color:"#000"}}
                            disabled={saving[u.id]}
                            onClick={() => saveTier(u.id, pendingTier)}
                          >{saving[u.id] ? "..." : "Save"}</button>
                          <button
                            style={{...styles.btn, fontSize:11, padding:"4px 8px", background:"#1a1a1a", color:"#666"}}
                            onClick={() => setEditingTier(et => { const n={...et}; delete n[u.id]; return n; })}
                          >✕</button>
                        </>
                      ) : (
                        <button
                          style={{...styles.btn, fontSize:11, padding:"4px 10px", background:"#1a1a1a", color:"#888"}}
                          onClick={() => setEditingTier(et => ({...et, [u.id]: u.tier || "lite"}))}
                        >Change Tier</button>
                      )}
                      <button
                        style={{...styles.btn, fontSize:11, padding:"4px 10px", background:"#1a1a1a", color:"#888"}}
                        onClick={() => openView(u.id)}
                      >View →</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} style={{...styles.td, textAlign:"center", color:"#444", padding:24}}>No users match</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Read-only user view modal (reused from LogsTab) */}
      {(viewLoading || viewingUser) && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={() => setViewingUser(null)}>
          <div style={{background:"#0f0f0f",border:"1px solid #222",borderRadius:12,padding:24,width:"100%",maxWidth:680,maxHeight:"calc(85vh / var(--ui-zoom, 1))",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div>
                <div style={{color:"#fff",fontWeight:700,fontSize:15}}>👁 Viewing: {viewingUser?.user?.display_name || viewingUser?.user?.id?.slice(0,12)}</div>
                <div style={{color:"#555",fontSize:11,marginTop:2}}>Read-only</div>
              </div>
              <button style={{background:"none",border:"none",color:"#555",cursor:"pointer",fontSize:20}} onClick={()=>setViewingUser(null)}>✕</button>
            </div>
            {viewLoading && <div style={{color:"#555",padding:20,textAlign:"center"}}>Loading...</div>}
            {viewingUser && <>
              <div style={{background:"#111",border:"1px solid #1a1a1a",borderRadius:8,padding:"12px 16px",marginBottom:12}}>
                <div style={{color:"#555",fontSize:10,letterSpacing:1,marginBottom:8}}>BOT STATUS</div>
                <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
                  {[["Running",viewingUser.status?.running?"Yes":"No"],["Paused",viewingUser.status?.paused?"Yes":"No"],["Listed",viewingUser.status?.stats?.listed??"-"],["Scanned",viewingUser.status?.stats?.scanned??"-"],["Tier",viewingUser.user?.tier||"-"]].map(([k,v])=>(
                    <div key={k}><div style={{color:"#555",fontSize:10}}>{k}</div><div style={{color:"#fff",fontSize:13,fontWeight:600}}>{String(v)}</div></div>
                  ))}
                </div>
              </div>
              <div style={{background:"#111",border:"1px solid #1a1a1a",borderRadius:8,padding:"12px 16px",marginBottom:12}}>
                <div style={{color:"#555",fontSize:10,letterSpacing:1,marginBottom:8}}>ROBLOX ACCOUNTS ({(viewingUser.config?.accounts||[]).length})</div>
                {(viewingUser.config?.accounts||[]).map((acc,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid #1a1a1a"}}>
                    {acc.AvatarUrl && <img src={acc.AvatarUrl} style={{width:28,height:28,borderRadius:"50%"}} alt="" />}
                    <div><div style={{color:"#fff",fontSize:13,fontWeight:600}}>{acc.Name||"Unknown"}</div><div style={{color:"#555",fontSize:11}}>Cookie: {acc.Cookie} · 2FA: {acc.Secret?"✓ set":"not set"}</div></div>
                  </div>
                ))}
              </div>
            </>}
          </div>
        </div>
      )}
    </>
  );
}

// ── Analytics ─────────────────────────────────────────────
function AnalyticsTab({ token }) {
  const [snapshots, setSnapshots] = useState([]);
  const [range, setRange] = useState("week");
  const [loading, setLoading] = useState(true);
  const [liveStats, setLiveStats] = useState(null);

  // Grab a fresh snapshot the moment this tab is actually opened, instead
  // of only relying on the hourly background job — which could otherwise
  // leave the page showing stale/empty data if nobody's checked in a while.
  useEffect(() => {
    adminReq("GET", "/admin/stats-live", token).then(setLiveStats).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    adminReq("GET", `/admin/stats-history?range=${range}`, token)
      .then(res => setSnapshots(res.snapshots || []))
      .catch(() => setSnapshots([]))
      .finally(() => setLoading(false));
  }, [range, liveStats]);

  const latest = liveStats || snapshots[snapshots.length - 1] || {};
  const usdValue = (latest.total_value_robux || 0) * (2.8 / 1000); // raw Rolimons value, $2.80 per 1,000 Robux
  const activeUsdValue = (latest.active_value_robux || 0) * (2.8 / 1000);
  const usdValueAfterFee = (latest.total_value_after_fee_robux || 0) * (5 / 1000); // after Roblox's ~30% marketplace fee, hardcoded $5 per 1,000 Robux
  const activeUsdValueAfterFee = (latest.active_value_after_fee_robux || 0) * (5 / 1000);

  const chartData = snapshots.map(s => ({
    time: new Date(s.created_at).toLocaleDateString(undefined, range === "week" ? {weekday:"short", hour:"numeric"} : {month:"short", day:"numeric"}),
    "Total Value (R)": s.total_value_robux,
    "Active Value (R)": s.active_value_robux,
    "Active Bots": s.active_bots,
    "Accounts Linked": s.total_accounts_linked,
  }));

  return (
    <>
      <div style={styles.cards}>
        {[
          ["Total Keys / Signups", latest.total_keys ?? "-"],
          ["Active Bots", latest.active_bots ?? "-"],
          ["Accounts Linked (All Time)", latest.total_accounts_linked ?? "-"],
          ["Total Value (Robux, All Time)", latest.total_value_robux?.toLocaleString() ?? "-"],
          ["Total Value (USD, All Time)", `$${usdValue.toLocaleString(undefined, {maximumFractionDigits:0})}`],
          ["Value Currently Running (Robux)", latest.active_value_robux?.toLocaleString() ?? "-"],
          ["Value Currently Running (USD)", `$${activeUsdValue.toLocaleString(undefined, {maximumFractionDigits:0})}`],
          ["Total Value After Fee (Robux)", latest.total_value_after_fee_robux?.toLocaleString() ?? "-"],
          ["Total Value After Fee (USD)", `$${usdValueAfterFee.toLocaleString(undefined, {maximumFractionDigits:0})}`],
          ["Value Currently Running After Fee (Robux)", latest.active_value_after_fee_robux?.toLocaleString() ?? "-"],
          ["Value Currently Running After Fee (USD)", `$${activeUsdValueAfterFee.toLocaleString(undefined, {maximumFractionDigits:0})}`],
        ].map(([l,v])=>(
          <div key={l} style={styles.card}>
            <div style={{color:"#555",fontSize:11,marginBottom:4}}>{l}</div>
            <div style={{color:"#fff",fontSize:22,fontWeight:700}}>{v}</div>
          </div>
        ))}
      </div>

      <div style={styles.section}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16}}>
          <h3 style={{...styles.sectionTitle, margin:0}}>Platform Growth</h3>
          <div style={styles.filterGroup}>
            {["week","month","year"].map(r => (
              <button
                key={r}
                style={{...styles.filterBtn, ...(range === r ? styles.filterBtnActive : {})}}
                onClick={() => setRange(r)}
              >
                {r[0].toUpperCase() + r.slice(1)}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div style={{color:"#444", padding:40, textAlign:"center"}}>Loading...</div>
        ) : chartData.length === 0 ? (
          <div style={{color:"#444", padding:40, textAlign:"center"}}>
            No snapshots yet. One gets recorded every hour, check back soon.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
              <XAxis dataKey="time" stroke="#555" fontSize={11} />
              <YAxis stroke="#555" fontSize={11} />
              <Tooltip contentStyle={{background:"#111", border:"1px solid #2a2a2a", borderRadius:8, fontSize:12}} />
              <Legend wrapperStyle={{fontSize:12}} />
              <Line type="monotone" dataKey="Total Value (R)" stroke="var(--rbx-accent, #38bdf8)" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="Active Value (R)" stroke="#a855f7" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="Active Bots" stroke="#f59e0b" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="Accounts Linked" stroke="#3b82f6" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </>
  );
}

// ── Logs ──────────────────────────────────────────────────
const USER_PALETTE = ["#38bdf8","#f472b6","#f97316","#a78bfa","#34d399","#fbbf24","#e879f9","#60a5fa"];

function LogsTab({ token }) {
  const [logs, setLogs]           = useState([]);
  const [logsPaused, setLogsPaused] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [focusUser, setFocusUser] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch]       = useState("");
  const [viewingUser, setViewingUser] = useState(null); // {user, config, status}
  const [viewLoading, setViewLoading] = useState(false);

  useEffect(() => {
    const poll = () => {
      if (!logsPaused) {
        adminReq("GET", "/admin/logs", token)
          .then(res => { setLogs(res); setFetchError(""); })
          .catch(e => setFetchError(e.message || "Failed to fetch logs"));
      }
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => clearInterval(iv);
  }, [logsPaused]);

  // Unique users in order of first appearance
  const activeUsers = [];
  const seen = new Set();
  for (const l of logs) {
    if (!seen.has(l.user_id)) { seen.add(l.user_id); activeUsers.push(l); }
  }

  // Stable color per user
  const colorOf = (uid) => USER_PALETTE[activeUsers.findIndex(u => u.user_id === uid) % USER_PALETTE.length];

  const filtered = logs.filter(l => {
    if (focusUser && l.user_id !== focusUser) return false;
    if (search && !l.msg?.toLowerCase().includes(search.toLowerCase()) &&
        !(l.display_name || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const focusedUser = activeUsers.find(u => u.user_id === focusUser);

  const openUserView = async (uid) => {
    setViewLoading(true);
    setShowPicker(false);
    try {
      const res = await adminReq("GET", `/admin/user-view/${uid}`, token);
      setViewingUser(res);
    } catch(e) {
      alert("Failed to load user view: " + e.message);
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <div style={styles.section}>
      {/* Header */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, flexWrap:"wrap", gap:8}}>
        <h3 style={{...styles.sectionTitle, margin:0}}>
          Live Logs: {focusUser ? <span style={{color: colorOf(focusUser)}}>{focusedUser?.display_name || focusUser?.slice(0,8)}</span> : `All Bots (${activeUsers.length} active)`}
        </h3>
        <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
          <input
            style={{...styles.input, width:160, fontSize:11, padding:"6px 10px"}}
            placeholder="Search logs..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button
            style={{...styles.btn, background:"#1a1a1a", color: focusUser ? colorOf(focusUser) : "#888", fontSize:11, padding:"6px 12px", border: focusUser ? `1px solid ${colorOf(focusUser)}` : "1px solid #2a2a2a"}}
            onClick={() => setShowPicker(true)}
          >
            {focusUser ? `👤 ${focusedUser?.display_name || focusUser?.slice(0,8)}` : "Focus User"}
          </button>
          {focusUser && (
            <button style={{...styles.btn, background:"#1a1a1a", color:"#666", fontSize:11, padding:"6px 10px"}} onClick={() => setFocusUser(null)}>✕ All</button>
          )}
          <button
            style={{...styles.btn, background: logsPaused ? "#7f1d1d" : "#1a1a1a", color: logsPaused ? "#fff" : "#888", fontSize:11, padding:"6px 12px"}}
            onClick={() => setLogsPaused(p => !p)}
          >
            {logsPaused ? "▶ Resume" : "⏸ Pause"}
          </button>
        </div>
      </div>

      {/* User chips */}
      {activeUsers.length > 0 && (
        <div style={{display:"flex", gap:6, marginBottom:10, flexWrap:"wrap"}}>
          {activeUsers.map(u => {
            const color = colorOf(u.user_id);
            const active = focusUser === u.user_id;
            const count = logs.filter(l => l.user_id === u.user_id).length;
            return (
              <button
                key={u.user_id}
                onClick={() => setFocusUser(active ? null : u.user_id)}
                style={{
                  padding:"4px 10px", borderRadius:20, border:`1px solid ${active ? color : "#222"}`,
                  background: active ? `${color}18` : "#111", color: active ? color : "#555",
                  cursor:"pointer", fontSize:11, fontWeight: active ? 700 : 400, fontFamily:"monospace",
                  display:"flex", alignItems:"center", gap:6,
                }}
              >
                <span style={{width:6, height:6, borderRadius:"50%", background:color, flexShrink:0}} />
                {u.display_name || u.user_id?.slice(0,8)}
                <span style={{opacity:0.5}}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Log box */}
      <div style={styles.logsBox}>
        {fetchError && <div style={{color:"#ef4444", padding:12}}>⚠ {fetchError}</div>}
        {!fetchError && filtered.length === 0 && <div style={{color:"#444", padding:12}}>{logs.length === 0 ? "No bots currently running" : "No lines match your filter"}</div>}
        {filtered.map((l, i) => {
          const color = colorOf(l.user_id);
          const msgColor = l.msg?.startsWith("[+]") || l.msg?.startsWith("[✓]") ? "#4ade80"
                         : l.msg?.startsWith("[-]") ? "#ef4444"
                         : l.msg?.startsWith("[!]") || l.msg?.startsWith("[⚠]") ? "#fbbf24"
                         : l.msg?.startsWith("[~]") ? "#888"
                         : "#ccc";
          return (
            <div key={i} style={{...styles.logLine, borderLeft: `2px solid ${color}20`, paddingLeft:8, marginBottom:3}}>
              <span style={styles.logTs}>{new Date(l.ts * 1000).toLocaleTimeString()}</span>
              <span style={{color, flexShrink:0, minWidth:90, fontWeight:600, fontSize:11}}>
                {l.display_name || l.user_id?.slice(0,8)}
              </span>
              <span style={{color: msgColor, wordBreak:"break-word"}}>{l.msg}</span>
            </div>
          );
        })}
      </div>

      {/* Focus user modal */}
      {showPicker && (
        <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center"}} onClick={() => setShowPicker(false)}>
          <div style={{background:"#111", border:"1px solid #222", borderRadius:12, padding:24, minWidth:340, maxWidth:480, width:"90%"}} onClick={e => e.stopPropagation()}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16}}>
              <div style={{color:"#fff", fontWeight:700, fontSize:15}}>Focus a User</div>
              <button style={{background:"none", border:"none", color:"#555", cursor:"pointer", fontSize:18}} onClick={() => setShowPicker(false)}>✕</button>
            </div>
            {activeUsers.length === 0 && <div style={{color:"#555", fontSize:13}}>No active bots right now.</div>}
            <div style={{display:"flex", flexDirection:"column", gap:8}}>
              {activeUsers.map(u => {
                const color = colorOf(u.user_id);
                const count = logs.filter(l => l.user_id === u.user_id).length;
                const active = focusUser === u.user_id;
                return (
                  <button
                    key={u.user_id}
                    onClick={() => { setFocusUser(active ? null : u.user_id); setShowPicker(false); }}
                    style={{
                      display:"flex", alignItems:"center", gap:12, padding:"12px 14px",
                      background: active ? `${color}12` : "#1a1a1a",
                      border:`1px solid ${active ? color : "#2a2a2a"}`,
                      borderRadius:8, cursor:"pointer", textAlign:"left",
                    }}
                  >
                    <span style={{width:10, height:10, borderRadius:"50%", background:color, flexShrink:0}} />
                    <div style={{flex:1}}>
                      <div style={{color: active ? color : "#fff", fontWeight:700, fontSize:13, fontFamily:"monospace"}}>
                        {u.display_name || u.user_id?.slice(0,8)}
                      </div>
                      <div style={{color:"#555", fontSize:11, marginTop:2}}>
                        {u.user_id?.slice(0,16)}… · {count} log lines
                      </div>
                    </div>
                    <button
                    onClick={e => { e.stopPropagation(); openUserView(u.user_id); }}
                    style={{padding:"4px 10px", background:"#111", border:"1px solid #333", borderRadius:6, color:"#888", cursor:"pointer", fontSize:11, flexShrink:0}}
                  >
                    View →
                  </button>
                  </button>
                );
              })}
            </div>
            {focusUser && (
              <button
                style={{marginTop:14, width:"100%", padding:"10px", background:"#1a1a1a", border:"1px solid #333", borderRadius:8, color:"#888", cursor:"pointer", fontSize:12}}
                onClick={() => { setFocusUser(null); setShowPicker(false); }}
              >
                Clear focus (show all users)
              </button>
            )}
          </div>
        </div>
      )}

      {/* Read-only user view modal */}
      {(viewLoading || viewingUser) && (
        <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20}} onClick={() => setViewingUser(null)}>
          <div style={{background:"#0f0f0f", border:"1px solid #222", borderRadius:12, padding:24, width:"100%", maxWidth:680, maxHeight:"calc(85vh / var(--ui-zoom, 1))", overflowY:"auto"}} onClick={e => e.stopPropagation()}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16}}>
              <div>
                <div style={{color:"#fff", fontWeight:700, fontSize:15}}>
                  👁 Viewing: {viewingUser?.user?.display_name || viewingUser?.user?.id?.slice(0,12)}
                </div>
                <div style={{color:"#555", fontSize:11, marginTop:2}}>Read-only: no changes possible from here</div>
              </div>
              <button style={{background:"none", border:"none", color:"#555", cursor:"pointer", fontSize:20}} onClick={() => setViewingUser(null)}>✕</button>
            </div>

            {viewLoading && <div style={{color:"#555", padding:20, textAlign:"center"}}>Loading...</div>}

            {viewingUser && <>
              {/* Bot status */}
              <div style={{background:"#111", border:"1px solid #1a1a1a", borderRadius:8, padding:"12px 16px", marginBottom:12}}>
                <div style={{color:"#555", fontSize:10, letterSpacing:1, marginBottom:8}}>BOT STATUS</div>
                <div style={{display:"flex", gap:20, flexWrap:"wrap"}}>
                  {[
                    ["Running", viewingUser.status?.running ? "Yes" : "No"],
                    ["Paused",  viewingUser.status?.paused  ? "Yes" : "No"],
                    ["Listed",  viewingUser.status?.stats?.listed ?? "-"],
                    ["Scanned", viewingUser.status?.stats?.scanned ?? "-"],
                    ["Tier",    viewingUser.user?.tier || "-"],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div style={{color:"#555", fontSize:10}}>{k}</div>
                      <div style={{color:"#fff", fontSize:13, fontWeight:600}}>{String(v)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Accounts */}
              <div style={{background:"#111", border:"1px solid #1a1a1a", borderRadius:8, padding:"12px 16px", marginBottom:12}}>
                <div style={{color:"#555", fontSize:10, letterSpacing:1, marginBottom:8}}>ROBLOX ACCOUNTS ({(viewingUser.config?.accounts || []).length})</div>
                {(viewingUser.config?.accounts || []).map((acc, i) => (
                  <div key={i} style={{display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid #1a1a1a"}}>
                    {acc.AvatarUrl && <img src={acc.AvatarUrl} style={{width:28, height:28, borderRadius:"50%"}} alt="" />}
                    <div>
                      <div style={{color:"#fff", fontSize:13, fontWeight:600}}>{acc.Name || "Unknown"}</div>
                      <div style={{color:"#555", fontSize:11}}>Cookie: {acc.Cookie} · 2FA: {acc.Secret ? "✓ set" : "not set"}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Key settings */}
              <div style={{background:"#111", border:"1px solid #1a1a1a", borderRadius:8, padding:"12px 16px"}}>
                <div style={{color:"#555", fontSize:10, letterSpacing:1, marginBottom:10}}>SETTINGS SNAPSHOT</div>
                <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 24px"}}>
                  {[
                    ["Item Delay",       viewingUser.config?.ItemDelay],
                    ["Loop Delay",       viewingUser.config?.PriceWarDelay],
                    ["RAP Floor %",      viewingUser.config?.RapFloorPercent],
                    ["Min Price Floor",  viewingUser.config?.MinPriceFloor],
                    ["Valued Items",     viewingUser.config?.ListValuedItems ? "On" : "Off"],
                    ["Floor Decay",      viewingUser.config?.FloorDecayEnabled ? "On" : "Off"],
                    ["Scan Method",      viewingUser.config?.InventoryScanMethod],
                    ["Autodiscover Loops", viewingUser.config?.AutodiscoverLoops],
                    ["Spiral Threshold", viewingUser.config?.SpiralResetThreshold],
                    ["Show Debug",       viewingUser.config?.ShowDebug ? "On" : "Off"],
                  ].map(([k, v]) => (
                    <div key={k} style={{display:"flex", justifyContent:"space-between", gap:8, padding:"5px 0", borderBottom:"1px solid #151515"}}>
                      <span style={{color:"#555", fontSize:12}}>{k}</span>
                      <span style={{color:"#ccc", fontSize:12, fontWeight:600}}>{v ?? "-"}</span>
                    </div>
                  ))}
                </div>

                {/* Items */}
                {viewingUser.config?.items?.length > 0 && (
                  <div style={{marginTop:12}}>
                    <div style={{color:"#555", fontSize:10, letterSpacing:1, marginBottom:6}}>ITEMS ({viewingUser.config.items.length})</div>
                    <div style={{display:"flex", flexWrap:"wrap", gap:4}}>
                      {viewingUser.config.items.map((item, i) => (
                        <span key={i} style={{background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:4, padding:"2px 8px", fontSize:11, color:"#888"}}>{item.name || item.id}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── My Accounts (Admin) ────────────────────────────────────
function MyAccountsTab({ token }) {
  const [accounts, setAccounts] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [sortBy, setSortBy] = useState("value"); // value | rap | items | name
  const [newFolder, setNewFolder] = useState("");
  const [dragging, setDragging] = useState(null); // username being dragged
  const [editingFolder, setEditingFolder] = useState(null);

  useEffect(() => { loadFolders(); loadAccounts(); }, []);

  const loadAccounts = async () => {
    setScanning(true);
    try {
      const res = await adminReq("GET", "/admin/my-accounts", token);
      setAccounts(res.accounts || []);
    } catch(e) { console.error(e); }
    setScanning(false); setLoading(false);
  };

  const loadFolders = async () => {
    try {
      const res = await adminReq("GET", "/admin/folders", token);
      setFolders(res.folders || []);
    } catch(e) {}
  };

  const saveFolders = async (updated) => {
    setFolders(updated);
    await adminReq("POST", "/admin/folders", token, { folders: updated });
  };

  const addFolder = () => {
    if (!newFolder.trim()) return;
    saveFolders([...folders, { name: newFolder.trim(), accounts: [] }]);
    setNewFolder("");
  };

  const removeFolder = (idx) => {
    const updated = folders.filter((_, i) => i !== idx);
    saveFolders(updated);
  };

  const moveToFolder = (username, folderIdx) => {
    const updated = folders.map((f, i) => ({
      ...f,
      accounts: i === folderIdx
        ? [...new Set([...f.accounts, username])]
        : f.accounts.filter(a => a !== username)
    }));
    saveFolders(updated);
  };

  const removeFromFolder = (username, folderIdx) => {
    const updated = folders.map((f, i) => ({
      ...f,
      accounts: i === folderIdx ? f.accounts.filter(a => a !== username) : f.accounts
    }));
    saveFolders(updated);
  };

  const sorted = [...accounts].sort((a, b) => {
    if (sortBy === "value") return (b.total_value || 0) - (a.total_value || 0);
    if (sortBy === "rap")   return (b.total_rap || 0) - (a.total_rap || 0);
    if (sortBy === "items") return (b.item_count || 0) - (a.item_count || 0);
    return a.username.localeCompare(b.username);
  });

  // Group into folders + unassigned
  const assigned = new Set(folders.flatMap(f => f.accounts));
  const unassigned = sorted.filter(a => !assigned.has(a.username));

  const totalValue = accounts.reduce((s, a) => s + (a.total_value || 0), 0);
  const totalRap   = accounts.reduce((s, a) => s + (a.total_rap || 0), 0);
  const totalUsd   = accounts.reduce((s, a) => s + (a.total_usd || 0), 0);
  const totalItems = accounts.reduce((s, a) => s + (a.item_count || 0), 0);

  if (loading) return <div style={{ color:"#555", padding:40 }}>Scanning accounts...</div>;

  const AccRow = ({ acc, folderIdx }) => (
    <div
      draggable
      onDragStart={() => setDragging(acc.username)}
      onDragEnd={() => setDragging(null)}
      style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", background: dragging===acc.username?"#1a1a1a":"#111", border:"1px solid #1e1e1e", borderRadius:8, marginBottom:8, cursor:"grab", opacity: dragging===acc.username?0.5:1 }}
    >
      {acc.avatar && <img src={acc.avatar} alt={acc.username} style={{ width:32, height:32, borderRadius:"50%", background:"#222", flexShrink:0 }} onError={e=>e.target.style.display="none"} />}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>{acc.username}</div>
        {acc.error && <div style={{ color:"#f87171", fontSize:11 }}>{acc.error}</div>}
      </div>
      {!acc.error && <>
        <div style={{ textAlign:"right", minWidth:70 }}>
          <div style={{ color:"var(--rbx-accent,#38bdf8)", fontSize:12, fontWeight:700 }}>{(acc.total_value||0).toLocaleString()} R$</div>
          <div style={{ color:"#555", fontSize:10 }}>{acc.item_count} items</div>
        </div>
        <div style={{ color:"#f59e0b", fontSize:12, fontWeight:700, minWidth:50, textAlign:"right" }}>${(acc.total_usd||0).toLocaleString()}</div>
      </>}
      {folderIdx !== undefined && (
        <button onClick={() => removeFromFolder(acc.username, folderIdx)} style={{ background:"none", border:"none", color:"#444", cursor:"pointer", fontSize:14, padding:"0 4px" }} title="Remove from folder">×</button>
      )}
    </div>
  );

  const FolderDrop = ({ folderIdx }) => (
    <div
      onDragOver={e => e.preventDefault()}
      onDrop={() => { if (dragging) moveToFolder(dragging, folderIdx); }}
      style={{ borderRadius:6, border:"1px dashed #2a2a2a", padding:"6px 10px", marginBottom:8, color:"#444", fontSize:11, textAlign:"center" }}
    >
      drop here
    </div>
  );

  return (
    <div style={{ width:"100%" }}>
      {/* Summary bar */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:24 }}>
        {[
          ["Total Value", `${totalValue.toLocaleString()} R$`, "var(--rbx-accent,#38bdf8)"],
          ["Total RAP",   `${totalRap.toLocaleString()} R$`,   "#fff"],
          ["Est. USD",    `$${totalUsd.toLocaleString()}`,      "#f59e0b"],
          ["Total Items", totalItems,                           "#888"],
        ].map(([label, val, color]) => (
          <div key={label} style={{ background:"#0d0d0d", border:"1px solid #1e1e1e", borderRadius:8, padding:"14px 18px" }}>
            <div style={{ color:"#555", fontSize:10, letterSpacing:1, marginBottom:6, textTransform:"uppercase" }}>{label}</div>
            <div style={{ color, fontSize:20, fontWeight:800, fontFamily:"monospace" }}>{val}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:20, alignItems:"flex-start" }}>
        {/* Left: sorted account list */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ color:"#555", fontSize:11, letterSpacing:1 }}>ALL ACCOUNTS ({accounts.length})</div>
            <div style={{ display:"flex", gap:6 }}>
              {["value","rap","items","name"].map(s => (
                <button key={s} onClick={() => setSortBy(s)} style={{ padding:"4px 10px", borderRadius:6, border:"none", cursor:"pointer", fontSize:11, fontWeight:600, background: sortBy===s?"var(--rbx-accent,#38bdf8)":"#1a1a1a", color: sortBy===s?"#000":"#666" }}>
                  {s}
                </button>
              ))}
              <button onClick={loadAccounts} disabled={scanning} style={{ padding:"4px 10px", borderRadius:6, border:"1px solid #2a2a2a", background:"#111", color:"#888", cursor:"pointer", fontSize:11 }}>
                {scanning ? "scanning..." : "↺ refresh"}
              </button>
            </div>
          </div>
          {sorted.map(acc => <AccRow key={acc.username} acc={acc} />)}
        </div>

        {/* Right: folders */}
        <div style={{ width:280, flexShrink:0 }}>
          <div style={{ color:"#555", fontSize:11, letterSpacing:1, marginBottom:14 }}>FOLDERS</div>

          {folders.map((folder, fi) => (
            <div key={fi} style={{ background:"#0d0d0d", border:"1px solid #1e1e1e", borderRadius:8, padding:12, marginBottom:12 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <div style={{ color:"#ccc", fontSize:13, fontWeight:700 }}>📁 {folder.name}</div>
                <button onClick={() => removeFolder(fi)} style={{ background:"none", border:"none", color:"#444", cursor:"pointer", fontSize:12 }}>✕</button>
              </div>
              {folder.accounts.map(username => {
                const acc = accounts.find(a => a.username === username);
                return acc ? <AccRow key={username} acc={acc} folderIdx={fi} /> : (
                  <div key={username} style={{ color:"#444", fontSize:12, padding:"6px 0" }}>{username}</div>
                );
              })}
              <FolderDrop folderIdx={fi} />
            </div>
          ))}

          <div style={{ display:"flex", gap:8, marginTop:8 }}>
            <input
              value={newFolder} onChange={e => setNewFolder(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addFolder()}
              placeholder="New folder name..."
              style={{ flex:1, background:"#111", border:"1px solid #222", borderRadius:6, padding:"8px 10px", color:"#fff", fontSize:12, outline:"none" }}
            />
            <button onClick={addFolder} style={{ background:"var(--rbx-accent,#38bdf8)", border:"none", borderRadius:6, padding:"8px 12px", color:"#000", fontWeight:700, fontSize:12, cursor:"pointer" }}>+</button>
          </div>
          <div style={{ color:"#444", fontSize:11, marginTop:8 }}>drag accounts from the left into folders</div>
        </div>
      </div>
    </div>
  );
}

// ── Support Tickets ────────────────────────────────────────
function SupportTab({ token }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState({});
  const [sending, setSending] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminReq("GET", "/admin/support", token);
      setTickets(res.tickets || []);
    } catch { setTickets([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const sendReply = async (id) => {
    const answer = (replyText[id] || "").trim();
    if (!answer) return;
    setSending(s => ({ ...s, [id]: true }));
    try {
      await adminReq("POST", `/admin/support/${id}/reply`, token, { answer });
      setTickets(ts => ts.map(t => t.id === id ? { ...t, answer, status: "answered" } : t));
      setReplyText(r => ({ ...r, [id]: "" }));
    } catch {}
    setSending(s => ({ ...s, [id]: false }));
  };

  const open = tickets.filter(t => t.status === "open");
  const answered = tickets.filter(t => t.status === "answered");

  return (
    <div style={{ width: "100%", maxWidth: 900 }}>
      <div style={{ ...styles.sectionTitle, marginBottom: 20, fontSize: 15, color: "#fff" }}>
        Support Questions: {open.length} open
      </div>

      {loading ? (
        <div style={{ color: "#555", fontSize: 13, padding: 20 }}>Loading…</div>
      ) : tickets.length === 0 ? (
        <div style={{ ...styles.section, color: "#555", fontSize: 13 }}>No support tickets yet.</div>
      ) : (
        <>
          {open.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ ...styles.sectionTitle, color: "var(--rbx-accent, #38bdf8)" }}>Open</div>
              {open.map(t => (
                <div key={t.id} style={{ ...styles.section, marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>{t.username || t.user_id}</span>
                    <span style={{ color: "#555", fontSize: 11 }}>{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</span>
                  </div>
                  <div style={{ color: "#ccc", fontSize: 13, marginBottom: 14, lineHeight: 1.6 }}>{t.question}</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      value={replyText[t.id] || ""}
                      onChange={e => setReplyText(r => ({ ...r, [t.id]: e.target.value }))}
                      onKeyDown={e => e.key === "Enter" && sendReply(t.id)}
                      placeholder="Type your reply…"
                      style={{ ...styles.input, flex: 1 }}
                    />
                    <button
                      onClick={() => sendReply(t.id)}
                      disabled={sending[t.id] || !(replyText[t.id] || "").trim()}
                      style={styles.btn}
                    >
                      {sending[t.id] ? "…" : "Reply"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {answered.length > 0 && (
            <details>
              <summary style={{ color: "#555", fontSize: 13, cursor: "pointer", marginBottom: 12 }}>
                {answered.length} answered ticket{answered.length !== 1 ? "s" : ""}
              </summary>
              {answered.map(t => (
                <div key={t.id} style={{ ...styles.section, marginBottom: 10, opacity: 0.7 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ color: "#888", fontWeight: 600, fontSize: 13 }}>{t.username || t.user_id}</span>
                    <span style={{ color: "#555", fontSize: 11 }}>{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</span>
                  </div>
                  <div style={{ color: "#999", fontSize: 13, marginBottom: 8 }}>{t.question}</div>
                  <div style={{ color: "var(--rbx-accent, #38bdf8)", fontSize: 13, borderLeft: "2px solid var(--rbx-accent, #38bdf8)", paddingLeft: 10 }}>
                    {t.answer}
                  </div>
                </div>
              ))}
            </details>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  wrap:        { minHeight:"calc(100vh / var(--ui-zoom, 1))", background:"#0a0a0a", color:"#fff", fontFamily:"monospace", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" },
  toast:       { position:"fixed", top:20, right:20, zIndex:9999, background:"#14532d", border:"1px solid var(--rbx-accent, #38bdf8)", color:"var(--rbx-accent, #38bdf8)", borderRadius:10, padding:"12px 18px", fontSize:13, fontWeight:600, boxShadow:"0 8px 32px rgba(0,0,0,0.5)" },
  tabBar:      { display:"flex", gap:4, background:"#111", border:"1px solid #1a1a1a", borderRadius:8, padding:4, marginBottom:20 },
  tabBtn:      { padding:"8px 20px", background:"transparent", border:"none", borderRadius:6, color:"#666", cursor:"pointer", fontSize:13, fontWeight:600 },
  tabBtnActive:{ background:"#1a1a1a", color:"var(--rbx-accent, #38bdf8)" },
  cards:       { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, width:"100%", marginBottom:24 },
  card:        { background:"#111", border:"1px solid #1a1a1a", borderRadius:8, padding:"14px 18px" },
  section:     { background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:8, padding:20, width:"100%", marginBottom:20, boxSizing:"border-box" },
  sectionTitle:{ color:"#888", fontSize:13, margin:"0 0 16px", letterSpacing:1 },
  input:       { background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:6, padding:"8px 12px", color:"#fff", fontSize:13, outline:"none" },
  select:      { background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:6, padding:"8px 12px", color:"#fff", fontSize:13, outline:"none" },
  btn:         { padding:"8px 16px", background:"var(--rbx-accent, #38bdf8)", border:"none", borderRadius:6, color:"#000", fontWeight:700, cursor:"pointer", fontSize:13 },
  deleteBtn:   { padding:"8px 16px", background:"#7f1d1d", border:"none", borderRadius:6, color:"#fff", fontWeight:700, cursor:"pointer", fontSize:12 },
  qtyWrap:     { display:"flex", alignItems:"center", background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:6, overflow:"hidden" },
  qtyBtn:      { width:30, height:34, background:"transparent", border:"none", color:"#888", cursor:"pointer", fontSize:16 },
  qtyInput:    { width:44, background:"transparent", border:"none", color:"#fff", fontSize:13, textAlign:"center", outline:"none" },
  genResult:   { marginTop:16, background:"#111", border:"1px solid #1a1a1a", borderRadius:8, padding:14 },
  genList:     { display:"flex", flexDirection:"column", gap:6, maxHeight:260, overflowY:"auto" },
  genRow:      { display:"flex", justifyContent:"space-between", alignItems:"center", background:"#1a1a1a", borderRadius:6, padding:"8px 12px" },
  rowCopyBtn:  { padding:"4px 10px", background:"#222", border:"1px solid #333", borderRadius:5, color:"#888", cursor:"pointer", fontSize:11 },
  copyAllBtn:  { padding:"5px 12px", background:"var(--rbx-accent, #38bdf8)", border:"none", borderRadius:6, color:"#000", fontWeight:700, cursor:"pointer", fontSize:11 },
  filterGroup: { display:"flex", background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:6, padding:2, gap:2 },
  filterBtn:   { padding:"6px 10px", background:"transparent", border:"none", borderRadius:5, color:"#666", cursor:"pointer", fontSize:11, fontWeight:600 },
  filterBtnActive: { background:"#111", color:"var(--rbx-accent, #38bdf8)" },
  badgeGreen:  { color:"var(--rbx-accent, #38bdf8)", fontSize:11, fontWeight:600 },
  badgeGray:   { color:"#555", fontSize:11 },
  table:       { width:"100%", borderCollapse:"collapse" },
  th:          { textAlign:"left", color:"#555", fontSize:11, padding:"6px 10px", borderBottom:"1px solid #1a1a1a", letterSpacing:1 },
  td:          { padding:"8px 10px", borderBottom:"1px solid #111", fontSize:12, color:"#ccc" },
  revealBtn:   { marginLeft:8, padding:"2px 8px", background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:4, color:"#666", cursor:"pointer", fontSize:10 },
  logsBox:     { background:"#0a0a0a", border:"1px solid #1a1a1a", borderRadius:8, padding:12, height:500, overflowY:"auto", fontFamily:"monospace" },
  logLine:     { display:"flex", gap:8, fontSize:12, marginBottom:4, whiteSpace:"pre-wrap", wordBreak:"break-word" },
  logTs:       { color:"#444", flexShrink:0 },
  logUser:     { color:"#666", flexShrink:0 },
  logDisplayName: { color:"var(--rbx-accent, #38bdf8)", fontWeight:600 },
};
